import { useCallback, useRef, useState } from 'react';

/**
 * Minimal shape shared by React synthetic and native mouse events. The preview
 * renders inside an iframe, so these handlers are attached as native listeners
 * on the iframe document rather than as React props.
 */
type PickerMouseEvent = Pick<
  globalThis.MouseEvent,
  'target' | 'preventDefault' | 'stopPropagation'
>;

export type PreviewHoverBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type PickedPreviewElement = {
  tagName: string;
  text?: string;
  dataQid?: string;
  nearestDataQid?: string;
  role?: string;
  ariaLabel?: string;
  className?: string;
  domPath: string;
};

function compactText(text: string | null | undefined, max = 500): string {
  return (text ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function compactClassName(el: Element): string | undefined {
  const cls = el.getAttribute('class');
  if (!cls) return undefined;
  return cls.split(/\s+/).filter(Boolean).slice(0, 12).join(' ');
}

function buildDomPath(el: Element, root: Element): string {
  const parts: string[] = [];
  let current: Element | null = el;
  while (current && current !== root && parts.length < 8) {
    const qid = current.getAttribute('data-qid');
    const tag = current.tagName.toLowerCase();
    if (qid) {
      parts.unshift(`${tag}[data-qid="${qid}"]`);
      break;
    }
    const cls = compactClassName(current);
    parts.unshift(cls ? `${tag}.${cls.split(' ').slice(0, 2).join('.')}` : tag);
    current = current.parentElement;
  }
  return parts.join(' > ');
}

function extractPreviewElementContext(
  el: HTMLElement,
  root: HTMLElement
): PickedPreviewElement {
  const nearestQidEl = el.closest('[data-qid]');
  const role = el.getAttribute('role') ?? undefined;
  const ariaLabel = el.getAttribute('aria-label') ?? undefined;
  const text = compactText(el.textContent);
  const className = compactClassName(el);

  return {
    tagName: el.tagName.toLowerCase(),
    ...(text ? { text } : {}),
    ...(el.dataset.qid ? { dataQid: el.dataset.qid } : {}),
    ...(nearestQidEl?.getAttribute('data-qid')
      ? { nearestDataQid: nearestQidEl.getAttribute('data-qid')! }
      : {}),
    ...(role ? { role } : {}),
    ...(ariaLabel ? { ariaLabel } : {}),
    ...(className ? { className } : {}),
    domPath: buildDomPath(el, root),
  };
}

export function formatPreviewElementContextForLlm(
  ctx: PickedPreviewElement | null
): string {
  if (!ctx) return '';
  return [
    '',
    '',
    'Selected preview element context:',
    JSON.stringify(ctx, null, 2),
    '',
    'Use this as visual/layout context for the user request. Prefer editing the associated layout_json node or nearest qid when relevant.',
  ].join('\n');
}

/**
 * `instanceof HTMLElement` is per-realm — inside the preview iframe the elements
 * belong to the frame's own `window`, so check against that realm's constructor.
 */
function isElementInRoot(
  target: EventTarget | null,
  root: HTMLElement
): target is HTMLElement {
  const view = root.ownerDocument.defaultView;
  if (!view || !(target instanceof view.HTMLElement)) return false;
  return root.contains(target);
}

export function usePreviewElementPicker() {
  const previewPaneRef = useRef<HTMLDivElement | null>(null);
  const [previewPickerActive, setPreviewPickerActive] = useState(false);
  const [previewHoverBox, setPreviewHoverBox] =
    useState<PreviewHoverBox | null>(null);
  const [pickedPreviewElement, setPickedPreviewElement] =
    useState<PickedPreviewElement | null>(null);

  const clearPreviewPicker = useCallback(() => {
    setPreviewPickerActive(false);
    setPreviewHoverBox(null);
    setPickedPreviewElement(null);
  }, []);

  const handlePreviewPickerMove = useCallback(
    (event: PickerMouseEvent) => {
      if (!previewPickerActive) return;
      const root = previewPaneRef.current;
      const target = event.target;
      if (!root || !isElementInRoot(target, root)) return;
      const targetRect = target.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      setPreviewHoverBox({
        left: targetRect.left - rootRect.left + root.scrollLeft,
        top: targetRect.top - rootRect.top + root.scrollTop,
        width: targetRect.width,
        height: targetRect.height,
      });
    },
    [previewPickerActive]
  );

  const handlePreviewPickerClick = useCallback(
    (event: PickerMouseEvent) => {
      if (!previewPickerActive) return;
      event.preventDefault();
      event.stopPropagation();

      const root = previewPaneRef.current;
      const target = event.target;
      if (!root || !isElementInRoot(target, root)) return;
      setPickedPreviewElement(extractPreviewElementContext(target, root));
      setPreviewPickerActive(false);
      setPreviewHoverBox(null);
    },
    [previewPickerActive]
  );

  const togglePreviewPicker = useCallback(() => {
    setPreviewPickerActive(v => !v);
    setPreviewHoverBox(null);
  }, []);

  return {
    previewPaneRef,
    previewPickerActive,
    previewHoverBox,
    pickedPreviewElement,
    setPickedPreviewElement,
    clearPreviewPicker,
    handlePreviewPickerMove,
    handlePreviewPickerClick,
    togglePreviewPicker,
    setPreviewHoverBox,
  };
}
